import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { CurrencyDollar, CreditCard, Bank, Warning, CheckCircle, Receipt, Copy } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ClientPaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentDialog, setPaymentDialog] = useState({ open: false, payment: null });
  const [bankDetails, setBankDetails] = useState(null);
  const [bankDialog, setBankDialog] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [paymentsRes, bankRes] = await Promise.all([
        axios.get(`${API}/api/client/payments`, { withCredentials: true }),
        axios.get(`${API}/api/client/bank-details`, { withCredentials: true }),
      ]);
      setPayments(paymentsRes.data.items || []);
      setBankDetails(bankRes.data);
    } catch {}
    setLoading(false);
  };

  const handleStripePayment = async (payment) => {
    setProcessingPayment(payment.id);
    try {
      const { data } = await axios.post(`${API}/api/client/pay/stripe`, {
        payment_id: payment.id,
        origin_url: window.location.origin,
      }, { withCredentials: true });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur Stripe');
    } finally {
      setProcessingPayment(null);
    }
  };

  const handleDownloadRecu = async (payment) => {
    try {
      const response = await axios.get(`${API}/api/client/pdf/recu/${payment.id}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `recu_${payment.reference_interne || payment.id}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Recu telecharge');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur telechargement');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copie !');
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#00AEEF]" /></div>;

  const pending = payments.filter(p => p.statut !== 'recu' && p.statut !== 'rapproche');
  const completed = payments.filter(p => p.statut === 'recu' || p.statut === 'rapproche');

  return (
    <div className="space-y-6 animate-fade-in" data-testid="client-payments-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>Mes paiements</h1>
          <p className="text-sm text-[#5E6D66] mt-1">Consultez et payez vos echeances en ligne</p>
        </div>
        <Button variant="outline" onClick={() => setBankDialog(true)} className="border-[#E8E6E1] text-xs" data-testid="client-view-rib-btn">
          <Bank size={14} className="mr-1.5" /> Voir le RIB
        </Button>
      </div>

      {/* Pending payments */}
      {pending.length > 0 && (
        <Card className="border-[#E8E6E1]" data-testid="client-pending-payments">
          <CardContent className="p-0">
            <div className="px-4 py-3 border-b border-[#E8E6E1] bg-amber-50/50">
              <p className="text-xs font-semibold text-amber-800">Paiements en attente ({pending.length})</p>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="border-[#E8E6E1]">
                  <TableHead className="text-[10px] uppercase text-[#5E6D66]">Reference</TableHead>
                  <TableHead className="text-[10px] uppercase text-[#5E6D66]">Type</TableHead>
                  <TableHead className="text-[10px] uppercase text-[#5E6D66]">Montant</TableHead>
                  <TableHead className="text-[10px] uppercase text-[#5E6D66]">Echeance</TableHead>
                  <TableHead className="text-[10px] uppercase text-[#5E6D66]">Statut</TableHead>
                  <TableHead className="text-[10px] uppercase text-[#5E6D66] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pending.map((p) => {
                  const remaining = (p.montant_attendu || 0) - (p.montant_recu || 0);
                  const isOverdue = p.date_echeance && new Date(p.date_echeance) < new Date();
                  return (
                    <TableRow key={p.id} className={`border-[#E8E6E1] ${isOverdue ? 'bg-red-50/40' : ''}`}>
                      <TableCell className="text-xs font-mono text-[#1A2421]">{p.reference_interne || '-'}</TableCell>
                      <TableCell><Badge variant="outline" className="text-[10px]">{p.type_flux}</Badge></TableCell>
                      <TableCell className="text-xs font-medium text-[#1A2421]">{remaining.toLocaleString('fr-FR')} €</TableCell>
                      <TableCell className={`text-xs ${isOverdue ? 'text-red-600 font-medium' : 'text-[#5E6D66]'}`}>
                        {p.date_echeance ? new Date(p.date_echeance).toLocaleDateString('fr-FR') : '-'}
                        {isOverdue && <Warning size={12} className="inline ml-1" />}
                      </TableCell>
                      <TableCell>
                        {isOverdue ? <Badge className="bg-red-100 text-red-700 text-[10px]">En retard</Badge> : <Badge className="bg-amber-100 text-amber-700 text-[10px]">En attente</Badge>}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1.5 justify-end">
                          <Button size="sm" className="h-7 bg-[#00AEEF] hover:bg-[#0095cc] text-[10px]" onClick={() => handleStripePayment(p)} disabled={processingPayment === p.id} data-testid={`client-pay-stripe-${p.id}`}>
                            {processingPayment === p.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <CreditCard size={12} className="mr-1" />}
                            Carte
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-[10px] border-[#E8E6E1]" onClick={() => setBankDialog(true)} data-testid={`client-pay-virement-${p.id}`}>
                            <Bank size={12} className="mr-1" /> Virement
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Completed payments */}
      <Card className="border-[#E8E6E1]" data-testid="client-completed-payments">
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b border-[#E8E6E1]">
            <p className="text-xs font-semibold text-[#1A2421]">Paiements recus ({completed.length})</p>
          </div>
          {completed.length === 0 ? (
            <div className="py-12 text-center"><p className="text-sm text-[#5E6D66]">Aucun paiement recu</p></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-[#E8E6E1]">
                  <TableHead className="text-[10px] uppercase text-[#5E6D66]">Reference</TableHead>
                  <TableHead className="text-[10px] uppercase text-[#5E6D66]">Montant</TableHead>
                  <TableHead className="text-[10px] uppercase text-[#5E6D66]">Date</TableHead>
                  <TableHead className="text-[10px] uppercase text-[#5E6D66]">Methode</TableHead>
                  <TableHead className="text-[10px] uppercase text-[#5E6D66] text-right">Recu</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {completed.map((p) => (
                  <TableRow key={p.id} className="border-[#E8E6E1]">
                    <TableCell className="text-xs font-mono text-[#1A2421]">{p.reference_interne || '-'}</TableCell>
                    <TableCell className="text-xs font-medium text-[#2D5A4C]">{(p.montant_recu || 0).toLocaleString('fr-FR')} €</TableCell>
                    <TableCell className="text-xs text-[#5E6D66]">{p.date_reception ? new Date(p.date_reception).toLocaleDateString('fr-FR') : '-'}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{p.methode || '-'}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" className="h-7 text-[10px] text-[#2D5A4C]" onClick={() => handleDownloadRecu(p)} data-testid={`client-download-recu-${p.id}`}>
                        <Receipt size={12} className="mr-1" /> PDF
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Bank details dialog */}
      <Dialog open={bankDialog} onOpenChange={setBankDialog}>
        <DialogContent className="sm:max-w-md" data-testid="client-bank-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Manrope' }}>
              <Bank size={18} className="text-[#2D5A4C]" /> Coordonnees bancaires
            </DialogTitle>
            <DialogDescription className="sr-only">Contenu de la boite de dialogue</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-[#5E6D66]">Pour effectuer un virement, utilisez les coordonnees suivantes :</p>
            {bankDetails && (bankDetails.iban || bankDetails.bic) ? (
              <div className="space-y-2.5">
                {[
                  { label: 'Titulaire', value: bankDetails.titulaire },
                  { label: 'Banque', value: bankDetails.banque },
                  { label: 'IBAN', value: bankDetails.iban },
                  { label: 'BIC/SWIFT', value: bankDetails.bic },
                ].map((item) => item.value && (
                  <div key={item.label} className="flex items-center justify-between p-2.5 rounded-lg bg-[#F9F8F5]">
                    <div>
                      <p className="text-[10px] text-[#5E6D66] uppercase">{item.label}</p>
                      <p className="text-xs font-mono font-medium text-[#1A2421]">{item.value}</p>
                    </div>
                    <button onClick={() => copyToClipboard(item.value)} className="text-[#5E6D66] hover:text-[#1A2421]"><Copy size={14} /></button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-[#5E6D66] text-center py-4">Coordonnees bancaires non configurees. Contactez l'administration.</p>
            )}
            <p className="text-[10px] text-[#5E6D66]">Mentionnez votre reference de paiement dans le libelle du virement.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBankDialog(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
